import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Target, Building2, Users as UsersIcon, User as UserIcon, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const fmtGBP = (n) =>
  n == null ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n));

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function currentQuarterKey() {
  // Xentra runs on UK fiscal year (April → March).
  //   FY Q1 = Apr–Jun, Q2 = Jul–Sep, Q3 = Oct–Dec, Q4 = Jan–Mar.
  // Period-key convention: `{FY-start-calendar-year}-Q{n}`, e.g. Jul 2026 = "2026-Q2".
  const d = new Date();
  const m = d.getMonth() + 1; // 1..12
  let fyStartYear, qIdx;
  if (m >= 4) {
    fyStartYear = d.getFullYear();
    qIdx = Math.floor((m - 4) / 3) + 1;
  } else {
    fyStartYear = d.getFullYear() - 1;
    qIdx = 4;
  }
  return `${fyStartYear}-Q${qIdx}`;
}
function currentFYKey() {
  // Xentra FY: labelled by end-year (e.g. "FY27" = April 2026 → March 2027)
  const d = new Date();
  const m = d.getMonth() + 1;
  const fyEndYear = m >= 4 ? d.getFullYear() + 1 : d.getFullYear();
  return `FY${String(fyEndYear).slice(-2)}`;
}

const DEFAULT_FORM = {
  scope: "individual",
  user_id: "",
  team_name: "",
  team_user_ids: [],
  metric: "mrr_closed_won",
  period: "month",
  period_key: currentMonthKey(),
  target_value: "",
};

export default function SalesTargetsAdminPanel() {
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [u, t] = await Promise.all([api.get("/users"), api.get("/sales-targets")]);
    setUsers(u.data);
    setTargets(t.data);
  };
  useEffect(() => { reload(); }, []);

  const updateForm = useCallback((k, v) => {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (k === "period") {
        next.period_key = v === "month" ? currentMonthKey()
          : v === "quarter" ? currentQuarterKey()
          : String(new Date().getFullYear());
      }
      if (k === "scope") {
        next.user_id = "";
        next.team_name = "";
        next.team_user_ids = [];
      }
      return next;
    });
  }, []);

  const toggleTeamUser = useCallback((uid) => {
    setForm((prev) => ({
      ...prev,
      team_user_ids: prev.team_user_ids.includes(uid)
        ? prev.team_user_ids.filter((x) => x !== uid)
        : [...prev.team_user_ids, uid],
    }));
  }, []);

  const save = async () => {
    if (!form.target_value) return toast.error("Enter a target value");
    if (form.scope === "individual" && !form.user_id) return toast.error("Select a user");
    if (form.scope === "team") {
      if (!form.team_name?.trim()) return toast.error("Enter a team name");
      if (form.team_user_ids.length === 0) return toast.error("Select at least one team member");
    }

    setSaving(true);
    try {
      const payload = {
        scope: form.scope,
        metric: form.metric,
        period: form.period,
        period_key: form.period_key,
        target_value: Number(form.target_value),
      };
      if (form.scope === "individual") payload.user_id = form.user_id;
      if (form.scope === "team") {
        payload.team_name = form.team_name.trim();
        payload.team_user_ids = form.team_user_ids;
      }
      await api.post("/sales-targets", payload);
      toast.success("Target saved");
      setForm({ ...DEFAULT_FORM, scope: form.scope, period: form.period, period_key: form.period_key, metric: form.metric });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this target?")) return;
    try { await api.delete(`/sales-targets/${id}`); await reload(); }
    catch (_) { toast.error("Failed"); }
  };

  const saveEdit = async (id, patch) => {
    try {
      await api.patch(`/sales-targets/${id}`, patch);
      toast.success("Target updated");
      await reload();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      return false;
    }
  };

  const grouped = useMemo(() => {
    const ind = [], team = [], biz = [];
    for (const t of targets) {
      const s = t.scope || "individual";
      if (s === "team") team.push(t);
      else if (s === "business") biz.push(t);
      else ind.push(t);
    }
    return { ind, team, biz };
  }, [targets]);

  return (
    <div className="space-y-5" data-testid="sales-targets-admin">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
          <Target className="h-3 w-3" /> Sales Targets
        </div>
        <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">Allocate targets</h3>
        <p className="text-xs text-slate-500 mt-1">
          Individuals, Teams (grouped people) or the whole Business. View progress on the Dashboard → Sales Targets tab.
        </p>
      </div>

      {/* New target form */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-sm font-heading">Allocate new target</CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          {/* Scope chips */}
          <div className="flex flex-wrap gap-2" data-testid="scope-chips">
            {[
              ["individual", "Individual", UserIcon],
              ["team", "Team", UsersIcon],
              ["business", "Business · All deals", Building2],
            ].map(([v, label, Icon]) => (
              <button
                key={v}
                data-testid={`scope-${v}`}
                type="button"
                onClick={() => updateForm("scope", v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition ${
                  form.scope === v ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Icon className="h-3 w-3" /> {label}
              </button>
            ))}
          </div>

          {/* Individual user selector */}
          {form.scope === "individual" && (
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">User</label>
              <Select value={form.user_id} onValueChange={(v) => updateForm("user_id", v)}>
                <SelectTrigger data-testid="target-user"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Team picker */}
          {form.scope === "team" && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-slate-500">Team name</label>
                <Input
                  data-testid="team-name"
                  value={form.team_name}
                  onChange={(e) => updateForm("team_name", e.target.value)}
                  placeholder="e.g. SDR Team, EMEA Sales"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-slate-500 mb-1.5 block">
                  Members ({form.team_user_ids.length} selected)
                </label>
                <div className="border border-slate-200 rounded-md max-h-44 overflow-auto p-2 grid grid-cols-2 gap-1.5 bg-slate-50" data-testid="team-members">
                  {users.map((u) => (
                    <label key={u.user_id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white px-2 py-1 rounded" data-testid={`team-member-${u.user_id}`}>
                      <Checkbox
                        checked={form.team_user_ids.includes(u.user_id)}
                        onCheckedChange={() => toggleTeamUser(u.user_id)}
                      />
                      <span className="truncate">{u.name || u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Metric / Period / Value */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Metric</label>
              <Select value={form.metric} onValueChange={(v) => updateForm("metric", v)}>
                <SelectTrigger data-testid="target-metric"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mrr_closed_won">MRR closed won</SelectItem>
                  <SelectItem value="mrr_renewals">Renewals MRR</SelectItem>
                  <SelectItem value="lead_count">Lead count</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Period</label>
              <Select value={form.period} onValueChange={(v) => updateForm("period", v)}>
                <SelectTrigger data-testid="target-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="quarter">Quarter</SelectItem>
                  <SelectItem value="year">Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Period key</label>
              <Input
                data-testid="target-period-key"
                value={form.period_key}
                onChange={(e) => updateForm("period_key", e.target.value)}
                placeholder="YYYY-MM / YYYY-Qn (fiscal) / YYYY"
              />
              {form.period === "quarter" && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Xentra FY: Q1 = Apr–Jun, Q2 = Jul–Sep, Q3 = Oct–Dec, Q4 = Jan–Mar. Year = FY-start (e.g. Jul 2026 → <code className="font-mono">2026-Q2</code>).
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="target-value"
                type="number"
                placeholder="Target"
                value={form.target_value}
                onChange={(e) => updateForm("target_value", e.target.value)}
              />
              <Button data-testid="target-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active targets — grouped sections */}
      <TargetsTable title="Business targets" icon={Building2} items={grouped.biz} onRemove={remove} onSaveEdit={saveEdit} fmtGBP={fmtGBP} testid="biz-targets-table" />
      <TargetsTable title="Team targets" icon={UsersIcon} items={grouped.team} onRemove={remove} onSaveEdit={saveEdit} fmtGBP={fmtGBP} testid="team-targets-table" />
      <TargetsTable title="Individual targets" icon={UserIcon} items={grouped.ind} onRemove={remove} onSaveEdit={saveEdit} fmtGBP={fmtGBP} testid="individual-targets-table" />
    </div>
  );
}

function TargetsTable({ title, icon: Icon, items, onRemove, onSaveEdit, fmtGBP, testid }) {
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({});

  const startEdit = (t) => {
    setEditId(t.id);
    setDraft({
      target_value: t.target_value,
      metric: t.metric,
      period: t.period,
      period_key: t.period_key,
    });
  };
  const cancelEdit = () => { setEditId(null); setDraft({}); };
  const commitEdit = async () => {
    if (draft.target_value === "" || draft.target_value == null) {
      toast.error("Target value is required"); return;
    }
    const ok = await onSaveEdit(editId, {
      target_value: Number(draft.target_value),
      metric: draft.metric,
      period: draft.period,
      period_key: draft.period_key,
    });
    if (ok) cancelEdit();
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100 py-3">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-slate-500" /> {title} ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm" data-testid={testid}>
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-4 py-2">Allocation</th>
              <th className="px-4 py-2">Metric</th>
              <th className="px-4 py-2">Period</th>
              <th className="px-4 py-2 text-right">Target</th>
              <th className="px-4 py-2 text-right w-[110px]"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-xs text-slate-400">None allocated.</td></tr>
            ) : items.map((t) => {
              const isEditing = editId === t.id;
              return (
                <tr key={t.id} className="border-b border-slate-100">
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">
                      {t.scope === "business" ? "All deals · whole business"
                        : t.scope === "team" ? t.team_name
                        : (t.user_name || t.display_name || "—")}
                    </div>
                    {t.scope === "team" && t.team_user_names?.length > 0 && (
                      <div className="text-[10px] text-slate-500 truncate max-w-[280px]">{t.team_user_names.join(", ")}</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {isEditing ? (
                      <Select value={draft.metric} onValueChange={(v) => setDraft((d) => ({ ...d, metric: v }))}>
                        <SelectTrigger data-testid={`edit-metric-${t.id}`} className="h-8 text-xs w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mrr_closed_won">MRR closed won</SelectItem>
                          <SelectItem value="mrr_renewals">Renewals MRR</SelectItem>
                          <SelectItem value="lead_count">Lead count</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        {t.metric === "lead_count" ? "Lead count"
                          : t.metric === "mrr_renewals" ? "Renewals MRR"
                          : "MRR closed won"}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {isEditing ? (
                      <div className="flex gap-1.5">
                        <Select value={draft.period} onValueChange={(v) => setDraft((d) => ({ ...d, period: v }))}>
                          <SelectTrigger data-testid={`edit-period-${t.id}`} className="h-8 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="month">Month</SelectItem>
                            <SelectItem value="quarter">Quarter</SelectItem>
                            <SelectItem value="year">Year</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          data-testid={`edit-period-key-${t.id}`}
                          className="h-8 text-xs w-[110px] font-mono"
                          value={draft.period_key}
                          onChange={(e) => setDraft((d) => ({ ...d, period_key: e.target.value }))}
                        />
                      </div>
                    ) : t.period_key}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {isEditing ? (
                      <Input
                        data-testid={`edit-value-${t.id}`}
                        type="number"
                        className="h-8 text-xs text-right"
                        value={draft.target_value}
                        onChange={(e) => setDraft((d) => ({ ...d, target_value: e.target.value }))}
                      />
                    ) : (t.metric === "lead_count" ? t.target_value : fmtGBP(t.target_value))}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      {isEditing ? (
                        <>
                          <button
                            data-testid={`edit-commit-${t.id}`}
                            onClick={commitEdit}
                            className="text-emerald-600 hover:text-emerald-700"
                            title="Save"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`edit-cancel-${t.id}`}
                            onClick={cancelEdit}
                            className="text-slate-500 hover:text-slate-700"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            data-testid={`target-edit-${t.id}`}
                            onClick={() => startEdit(t)}
                            className="text-slate-400 hover:text-blue-600"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            data-testid={`target-delete-${t.id}`}
                            onClick={() => onRemove(t.id)}
                            className="text-slate-400 hover:text-rose-600"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
